'use client';

import type { PaginatedProduct, Swatches } from '@dealerportal/common';
import { useTranslations } from '@dealerportal/i18n/client';
import {
  useClientContext,
  useFavouriteProducts,
} from '@dealerportal/react-helpers';
import { remapBrandName, remapProductName } from '@dealerportal/utilities';
import classNames from 'classnames';

import { useProductLink } from '@dealerportal/catalog-ui/Hooks/useProductLink';
import { useVariantLoader } from '@dealerportal/catalog-ui/Hooks/useVariantLoader';
import { BetterButton } from '@dealerportal/ui-components/BetterButton';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useEffect, type FC } from 'react';
import { toast } from 'react-toastify';
import { MarkFavourite } from './MarkFavourite';
import { MissingImage } from './MissingImage';
import { ProductCardSwatches } from './ProductCardSwatches';
import { ProductLink } from './ProductLink';
import { ProductTags } from './ProductTags';
import { Samples } from './Samples';

export interface Product {
  swatchImage: string;
}

interface Props {
  product: PaginatedProduct;
  showSwatches?: boolean;
  disableOrderSample?: boolean;
  hidePricing?: boolean;
  imageClassNames?: string;
}

export const ProductCard: FC<Props> = ({
  product,
  showSwatches = false,
  disableOrderSample: disableOrderSampleProp,
  hidePricing = false,
  imageClassNames,
}: Props) => {
  const t = useTranslations('products');
  const [currentProduct, setCurrentProduct] = React.useState(product);
  const [isOpen, setIsOpen] = React.useState(false);
  const [showButtonLoader, setShowButtonLoader] = React.useState(false);
  const favouriteContext = useFavouriteProducts();
  const ctx = useClientContext();
  const disableOrderSample = disableOrderSampleProp ?? ctx.disableOrderSample;
  const { generateProductLink } = useProductLink();
  const {
    ref: variantRef,
    swatches: loadedSwatches,
    colorCount,
  } = useVariantLoader(product);

  useEffect(() => {
    setCurrentProduct(product);
  }, [product]);

  const handleFavouriteClick = useCallback(() => {
    if (favouriteContext?.addRemoveProductsFromFav) {
      const isFavourite = favouriteContext.find({ UPID: currentProduct.UPID });
      favouriteContext.addRemoveProductsFromFav(currentProduct);

      if (!isFavourite) {
        toast(t('addedToFavorites'));
      }
    }
  }, [currentProduct, favouriteContext]);

  const swatchLimit = 7;
  const uniqueProductSwatches = uniqBy(loadedSwatches, 'UPID')?.sort((a, b) =>
    a.colorName.localeCompare(b.colorName)
  );
  const [swatches, setSwatches] = React.useState(
    uniqueProductSwatches.slice(0, swatchLimit)
  );

  useEffect(() => {
    const updated = uniqBy(loadedSwatches, 'UPID')?.sort((a, b) =>
      a.colorName.localeCompare(b.colorName)
    );
    setSwatches(updated.slice(0, swatchLimit));
  }, [loadedSwatches]);

  const handleSwatchClick = useCallback(() => {
    swatches.length < uniqueProductSwatches.length
      ? setSwatches(uniqueProductSwatches)
      : setSwatches(uniqueProductSwatches.slice(0, swatchLimit));
  }, [swatches, uniqueProductSwatches]);

  let tags = currentProduct?.tags ?? [];
  tags = tags.filter((t) => t !== 'Order_Sample');
  const sampleSwatches: Swatches[] = uniqueProductSwatches.filter(
    (x) => x.hasSample
  );

  // The current product may not be in uniqueProductSwatches (e.g. spread colors, or products
  // whose swatches haven't loaded yet), so add it explicitly if it has a sample and is absent,
  // then re-sort to keep the list in colorName order for ProductQuickViewDialog.
  if (
    currentProduct.hasSample &&
    !sampleSwatches.some((s) => s.UPID === currentProduct.UPID)
  ) {
    sampleSwatches.push({ ...currentProduct });
    sampleSwatches.sort((a, b) => a.colorName.localeCompare(b.colorName));
  }

  const vizToken =
    ctx.visualizerSettings !== undefined
      ? ctx.visualizerSettings.showVisualizer
        ? (ctx.visualizerSettings.apiKey ?? '')
        : ''
      : (ctx.tokens.find((x) => x.TokenName === 'ff-viz-api-token')
          ?.Replacement ?? '');

  return (
    <div
      ref={variantRef}
      key={`${currentProduct.id}`}
      className="product-card ui-justify-between ui-rounded ui-overflow-hidden ui-bg-white"
    >
      <div className="ui-h-full ui-flex ui-flex-col">
        <div className="ui-relative">
          <div className="!ui-absolute ui-p-2 ui-w-full ui-box-border">
            <div className="ui-relative ui-flex ui-gap-2 ui-w-full ui-items-start ui-justify-between">
              <div className="ui-flex ui-gap-2">
                {tags?.length ? (
                  <ProductTags
                    isOpen={isOpen}
                    onTagClick={() => setIsOpen((prev) => !prev)}
                    tags={tags}
                  />
                ) : null}
              </div>
              {favouriteContext && (
                <MarkFavourite
                  btnClass={classNames(
                    '!ui-min-w-8 !ui-min-h-8 !ui-w-8 !ui-h-8 ui-shrink-0',
                    isOpen ? '!ui-hidden' : 'ui-block'
                  )}
                  iconClass="!ui-w-5 !ui-h-5"
                  isFavourite={favouriteContext?.find({
                    UPID: currentProduct.UPID,
                  })}
                  onClick={handleFavouriteClick}
                />
              )}
            </div>
          </div>

          <ProductLink
            product={currentProduct}
            className="ui-h-[100%] ui-block ui-z-[100]"
            id={`product-link-${currentProduct.UPID}`}
          >
            {!currentProduct.imageURL && (
              <MissingImage
                className={classNames(
                  'product-card__image ui-fill-gray-400/50 ui-bg-gray-100',
                  imageClassNames
                )}
                height={290}
                width={290}
              />
            )}
            {currentProduct.imageURL && (
              <img
                className={classNames(
                  'product-card__image ui-object-cover',
                  imageClassNames
                )}
                alt={`${currentProduct.productName} by ${currentProduct.brandName}`}
                title={`${currentProduct.productName} by ${currentProduct.brandName}`}
                style={{ width: '100%' }}
                src={
                  currentProduct.imageURL.includes('res.cloudinary.com')
                    ? currentProduct.imageURL.replace(
                        'https://images.floorforce.com/Textures/',
                        ''
                      )
                    : currentProduct.imageURL
                }
                height={290}
                width={290}
              />
            )}
          </ProductLink>
        </div>

        <div className="ui-flex-1 ui-flex ui-flex-col">
          <div
            className={classNames('ui-p-4 ui-pb-6 ui-space-y-2', {
              '!ui-pb-4': currentProduct.hasVisualization,
            })}
          >
            <div className="ui-flex ui-items-center ui-text-sm ui-font-semibold ui-text-gray-700 ui-gap-1 ui-flex-wrap">
              <span className="product-card__brand">
                {remapBrandName(currentProduct.brandName)}
              </span>
              {!showSwatches && colorCount > 1 && (
                <ProductLink
                  product={currentProduct}
                  className="product-card__colors-link !ui-flex !ui-items-center ui-gap-1.5 !ui-text-xs ui-ml-auto ui-whitespace-nowrap !ui-font-bold !ui-text-gray-400 ui-font-[inherit]"
                >
                  <span>{t('seeColors', { count: colorCount })}</span>
                  <svg
                    className="ui-w-2 ui-mt-px ui-shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 320 512"
                  >
                    <path
                      className="ui-fill-gray-400"
                      d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z"
                    />
                  </svg>
                </ProductLink>
              )}
            </div>

            <div>
              <ProductLink
                product={currentProduct}
                className="!ui-font-semibold !ui-text-gray-900"
              >
                <div className="product-card__line">
                  {remapProductName(currentProduct.productName)}
                </div>

                <div className="product-card__color ui-font-normal">
                  {currentProduct.colorName}
                </div>

                {currentProduct.additionalMessage && (
                  <div className="product-card__color ui-font-normal ui-mt-1 ui-text-sm product-card__additional-message">
                    {currentProduct.additionalMessage}
                  </div>
                )}
              </ProductLink>

              {showSwatches && swatches?.length > 1 && (
                <div className="product-card__swatch_thumbnails ui-flex ui-gap-2 ui-mt-4">
                  <ProductCardSwatches
                    swatches={swatches}
                    selectedSwatch={currentProduct}
                    onMouseEnter={(swatch) => {
                      setCurrentProduct({
                        ...currentProduct,
                        ...swatch,
                      });

                      if (
                        currentProduct.hasSample &&
                        swatch.UPID !== currentProduct.UPID
                      ) {
                        setShowButtonLoader(true);

                        setTimeout(() => {
                          setShowButtonLoader(false);
                        }, 250);
                      }
                    }}
                    onSelect={(swatch) => {
                      // On mobile, override onclick to switch the current color in the card rather than go to the color's PDP page
                      if (!window.matchMedia('(min-width: 768px)').matches) {
                        setCurrentProduct({
                          ...currentProduct,
                          ...swatch,
                        });
                      }
                    }}
                  />
                  {uniqueProductSwatches.length > swatchLimit && (
                    <BetterButton
                      btnStyle="simple"
                      label={
                        swatches.length !== uniqueProductSwatches.length
                          ? `+${uniqueProductSwatches.length - swatchLimit}`
                          : `<svg class="ui-w-3 ui-fill-gray-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M432 256c0 17.7-14.3 32-32 32L48 288c-17.7 0-32-14.3-32-32s14.3-32 32-32l352 0c17.7 0 32 14.3 32 32z"/></svg>`
                      }
                      className="ui-shrink-0 ui-h-6 ui-w-6 ui-items-center ui-justify-center ui-rounded-full"
                      labelClassName="ui-flex ui-items-center ui-justify-center ui-text-xs ui-text-gray-700 ui-font-semibold"
                      attributes={{
                        onClick: handleSwatchClick,
                      }}
                    />
                  )}
                </div>
              )}

              {!hidePricing &&
                currentProduct.unitPrice > 0 &&
                currentProduct.priceDescription && (
                  <div className="product-card__price ui-mt-4">
                    {currentProduct.unitPrice && (
                      <div className="ui-flex ui-items-center ui-gap-1.5">
                        <div className="ui-text-xl ui-font-bold ui-leading-none ui-text-gray-800">
                          {`$${currentProduct.unitPrice.toFixed(2)}`}
                        </div>
                        /
                        <div className="ui-relative ui-text-xs ui-text-gray-800 ui-font-bold ui-leading-none">
                          {currentProduct.priceDescription}
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
          </div>

          {(sampleSwatches.length > 0 || currentProduct.hasVisualization) && (
            <div className="ui-flex ui-flex-wrap ui-gap-1 ui-pb-4 ui-pt-1 ui-px-4 ui-mt-auto">
              {!disableOrderSample &&
                sampleSwatches.map((swatch) => (
                  <Samples
                    key={swatch.UPID}
                    samplePrice={0}
                    productInfo={swatch}
                    visible={currentProduct.UPID === swatch.UPID}
                    isLoading={showButtonLoader}
                    replaceClass={{
                      'ui-h-12': 'ui-h-10',
                      '!ui-px-6': 'ui-px-3',
                    }}
                  />
                ))}

              {currentProduct.hasVisualization && vizToken && (
                <div className="product-card__viz-button">
                  <BetterButton
                    btnStyle={currentProduct.hasSample ? 'neutral' : 'primary'}
                    href={`${generateProductLink(currentProduct)}#vis`}
                    label={t('seeInYourRoom')}
                    replaceClass={{
                      'ui-h-12': 'ui-h-10',
                      '!ui-px-6': currentProduct.hasSample
                        ? 'ui-px-2.5'
                        : 'ui-px-3',
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
